import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useApp } from "../hooks/useApp";
import { useBrandingContext } from "../contexts/BrandingContext";
import NotificationSettings from "./NotificationSettings";

function LogoWithFallback() {
  const [logoError, setLogoError] = useState(false);
  const { branding } = useBrandingContext();

  return (
    <Link
      to="/"
      className="flex items-center hover:opacity-80 transition-opacity"
    >
      {!logoError ? (
        <img
          src={branding.logo_url}
          alt={branding.site_name}
          className="h-8 w-auto"
          onError={() => setLogoError(true)}
        />
      ) : (
        <span className="text-xl font-bold text-foreground">{branding.site_name}</span>
      )}
    </Link>
  );
}

function Header() {
  const {
    user,
    isAuthenticated,
    logout,
    isDevelopmentMode,
    refreshUserProfile,
  } = useAuth();
  const { llmProviderName } = useApp();
  const [showCreditModal, setShowCreditModal] = useState(false);

  const handleLogout = () => {
    logout();
  };

  const handleAddCredits = () => {
    setShowCreditModal(true);
  };

  return (
    <header className="bg-card shadow-sm border-b border-border">
      <div className="px-3 sm:px-6">
        <div className="flex justify-between items-center h-16 gap-2">
          {/* Logo and Title */}
          <div className="flex items-center space-x-4">
            <div className="flex items-center">
              <LogoWithFallback />
              {isDevelopmentMode && (
                <span className="ml-2 px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded font-medium">
                  DEV MODE
                </span>
              )}
            </div>
          </div>

          {/* User Menu */}
          <div className="flex items-center space-x-4">
            {isAuthenticated ? (
              <UserMenu
                user={user}
                onLogout={handleLogout}
                onAddCredits={handleAddCredits}
                isDevelopmentMode={isDevelopmentMode}
              />
            ) : (
              <AuthLinks />
            )}
          </div>
        </div>
      </div>

    </header>
  );
}

function UserMenu({ user, onLogout, onAddCredits, isDevelopmentMode }) {
  return (
    <div className="flex items-center space-x-1 sm:space-x-3">
      {/* Admin Link - Only show for admin users */}
      {user?.profile?.is_admin && (
        <Link
          to="/admin"
          className="inline-flex items-center justify-center bg-purple-600 hover:bg-purple-700 text-white rounded-md text-sm px-3 py-1.5 font-medium transition-colors duration-200"
          title="Admin Dashboard"
        >
          Admin
        </Link>
      )}

      {/* Notification Settings */}
      <NotificationSettings />

      <a
        href="mailto:support@discoseo.com"
        className="inline-flex items-center justify-center text-card-foreground hover:text-foreground hover:bg-gray-100 rounded-md text-sm px-3 py-2 font-medium transition-colors duration-200"
      >
        Contact
      </a>

      {/* Logout Button */}
      <button
        onClick={onLogout}
        className="inline-flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-gray-100 rounded-md text-sm px-4 py-2 transition-colors duration-200"
      >
        Logout
      </button>
    </div>
  );
}

function AuthLinks() {
  return (
    <div className="flex items-center space-x-1 sm:space-x-3">
      <a
        href="mailto:support@discoseo.com"
        className="inline-flex items-center justify-center text-card-foreground hover:text-foreground hover:bg-gray-100 rounded-md px-4 py-2 text-sm font-medium transition-colors duration-200"
      >
        Contact
      </a>
      <Link
        to="/login"
        className="inline-flex items-center justify-center text-card-foreground hover:text-foreground hover:bg-gray-100 rounded-md px-4 py-2 text-sm font-medium transition-colors duration-200"
      >
        Login
      </Link>
      <Link
        to="/signup"
        className="inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white rounded-md px-4 py-2 text-sm font-medium transition-colors duration-200"
      >
        Sign Up
      </Link>
    </div>
  );
}

export default Header;
